import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { FaEye, FaDownload, FaFileExcel } from "react-icons/fa";
import * as XLSX from "xlsx";
import "../../../styles/dashboard/shared/CreditReportManagement.css";
import PdfViewerModal from "../../PdfViewerModal/PdfViewerModal";

export default function CreditReportManagement({ 
  role = "orguser", 
  authService,
  apiBaseUrl = process.env.REACT_APP_API_URL
}) {
  const getProviderLabel = (report = {}) => {
    const planType = String(report.planType || '').toLowerCase();
    if (planType === 'basic') {
      return 'LCS';
    }

    const rawProvider = String(report.apiProvider || '').trim();
    if (!rawProvider) {
      return 'N/A';
    }

    const normalized = rawProvider.toLowerCase();
    if (normalized === 'none' || normalized === 'null' || normalized === 'undefined' || normalized === 'n/a') {
      return 'N/A';
    }

    return rawProvider.toUpperCase();
  };

  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [reports, setReports] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const reportsPerPage = 10;
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);

  // Modal state
  const [isPdfViewerOpen, setIsPdfViewerOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  // Toast
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  // Fetch organizations for admin role
  useEffect(() => {
    if (role === "admin") {
      fetchOrganizations();
    }
  }, [role]);

  // Fetch reports when dependencies change
  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, selectedOrgId, currentPage, searchTerm, dateFrom, dateTo]);

  const fetchOrganizations = async () => {
    try {
      const endpoint = `${apiBaseUrl}/admin/organizations`;
      const token = authService.getAuthHeader();
      const response = await fetch(`${endpoint}?page=1&limit=100`, { headers: token });
      const data = await response.json();
      if (data.success) {
        setOrganizations(data.data || []);
        // Don't auto-select organization - show all by default
      }
    } catch (error) {
      console.error("Fetch orgs error:", error);
      showToast("Failed to load organizations", "error");
    }
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      let endpoint;
      let params = new URLSearchParams({
        page: currentPage,
        limit: reportsPerPage,
        search: searchTerm,
      });

      // Add date range filters
      if (dateFrom) {
        params.append("dateFrom", dateFrom);
      }
      if (dateTo) {
        params.append("dateTo", dateTo);
      }

      // Determine endpoint based on role
      if (role === "superadmin") {
        endpoint = `${apiBaseUrl}/superadmin/credit-reports`;
      } else if (role === "admin") {
        endpoint = `${apiBaseUrl}/admin/credit-reports`;
        // Only add orgId if an organization is selected
        if (selectedOrgId) {
          params.append("orgId", selectedOrgId);
        }
      } else {
        // orguser - always filtered by their organization
        endpoint = `${apiBaseUrl}/orguser/credit-reports`;
      }
      
      const fullUrl = `${endpoint}?${params}`;
      
      const token = authService.getAuthHeader();
      const response = await fetch(fullUrl, { headers: token });
      const data = await response.json();
      
      if (data.success || data.message === 'Credit reports retrieved successfully') {
        const rawData = data.data || [];
        const normalized = rawData.map((r) => {
          const customerName = r.customerName || r.entityName || "N/A";
          
          return {
            id: r.id,
            reportId: r.id,
            creditCheckId: r.creditCheckId || r.id,
            customerName,
            organizationName: r.organizationName || r.creditCheck?.organization?.organizationName || "N/A",
            performedByName: r.performedByName || r.creditCheck?.performedBy?.fullName || "N/A",
            planType: r.planType || "Basic",
            apiProvider: r.apiProvider || null,
            creditCost: r.creditCost || "0.00",
            createdAt: r.createdAt,
            pdfPath: r.pdfPath,
            panNumber: r.panNumber,
            loanAmount: r.loanAmount,
          };
        });
        setReports(normalized);
        setTotalRecords(data.pagination?.totalRecords || data.pagination?.total || rawData.length);
      } else {
        showToast(data.error || "Failed to load reports", "error");
        setReports([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error("Fetch reports error:", error);
      showToast("Failed to load reports", "error");
      setReports([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (msg, type = "success") => {
    setToast({ show: true, message: msg, type });
    setTimeout(() => setToast({ show: false }), 3000);
  };

  // Filter reports by grade (client-side filter for already fetched data)
  const filteredReports = reports;

  // Pagination
  const totalPages = Math.ceil(totalRecords / reportsPerPage);

  const handleViewPdf = async (report) => {
    try {
      if (!report.pdfPath) {
        showToast("PDF is still generating. Please try again in a few seconds.", "error");
        return;
      }
      
      let endpoint;
      if (role === "orguser") {
        endpoint = `${apiBaseUrl}/orguser/credit-checks/${report.creditCheckId}/download`;
      } else if (role === "superadmin") {
        endpoint = `${apiBaseUrl}/superadmin/credit-checks/${report.id}/download`;
      } else {
        endpoint = `${apiBaseUrl}/admin/credit-checks/${report.id}/download`;
      }
      
      const token = authService.getAuthHeader();
      const response = await fetch(endpoint, { headers: token });
      const data = await response.json();
      
      const url = data?.data?.url || data?.url || data?.downloadUrl || data?.pdfUrl;
      if (url) {
        window.open(url, '_blank');
      } else {
        showToast("PDF not available yet", "error");
      }
    } catch (err) {
      console.error("Failed to view credit report PDF", err);
      showToast("Failed to load PDF report. Please try again.", "error");
    }
  };

  const handleDownload = async (report) => {
    try {
      if (!report.pdfPath) {
        showToast("PDF is still generating. Please try again.", "error");
        return;
      }
      
      let endpoint;
      if (role === "orguser") {
        endpoint = `${apiBaseUrl}/orguser/credit-checks/${report.creditCheckId}/download`;
      } else if (role === "superadmin") {
        endpoint = `${apiBaseUrl}/superadmin/credit-checks/${report.id}/download`;
      } else {
        endpoint = `${apiBaseUrl}/admin/credit-checks/${report.id}/download`;
      }
      
      const token = authService.getAuthHeader();
      const response = await fetch(endpoint, { headers: token });
      const data = await response.json();
      
      const url = data?.data?.url || data?.url || data?.downloadUrl;
      if (url) {
        window.open(url, "_blank");
        showToast("Download started", "success");
      } else {
        showToast("Download URL not available", "error");
      }
    } catch (err) {
      console.error("Failed to download credit report", err);
      showToast("Failed to download report", "error");
    }
  };

  const handleExportToExcel = () => {
    if (reports.length === 0) {
      showToast("No data to export", "error");
      return;
    }

    // Prepare data for Excel
    const excelData = reports.map((report, index) => ({
      "S.No": index + 1,
      "Report ID": report.reportId,
      "Customer Name": report.customerName,
      "Organization Name": report.organizationName,
      "Orguser Name": report.performedByName,
      "Credit Score Type": report.planType,
      "Provider": getProviderLabel(report),
      "Cost": report.creditCost,
      "PAN Number": report.panNumber || "N/A",
      "Loan Amount": report.loanAmount || "N/A",
      "Date & Time": report.createdAt
        ? new Date(report.createdAt).toLocaleString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          })
        : "-"
    }));

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    
    // Set column widths
    const columnWidths = [
      { wch: 8 },  // S.No
      { wch: 12 }, // Report ID
      { wch: 25 }, // Customer Name
      { wch: 30 }, // Organization Name
      { wch: 25 }, // Orguser Name
      { wch: 18 }, // Credit Score Type
      { wch: 14 }, // Provider
      { wch: 12 }, // Cost
      { wch: 15 }, // PAN Number
      { wch: 15 }, // Loan Amount
      { wch: 22 }  // Date & Time
    ];
    worksheet['!cols'] = columnWidths;

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Credit Reports");

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `Credit_Reports_${role}_${timestamp}.xlsx`;

    // Save file
    XLSX.writeFile(workbook, filename);
    showToast("Excel file downloaded successfully", "success");
  };

  return (
    <div className="credit-reports-page">
      {/* TOAST */}
      {toast.show && (
        <div className={`toast-notification ${toast.type === "success" ? "toast-success" : "toast-error"}`}>
          <span>{toast.message}</span>
        </div>
      )}

      <div className="page-header">
        <div>
          <h1>Credit Reports</h1>
          <p>View generated credit assessment reports</p>
        </div>
        <div className="header-actions">
          {/* Organization Selector - Only for Admin */}
          {role === "admin" && organizations.length > 0 && (
            <div className="header-org-selector">
              <select
                className="filter-select org-select"
                value={selectedOrgId}
                onChange={(e) => {
                  setSelectedOrgId(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">All Organizations</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.organizationName}
                  </option>
                ))}
              </select>
            </div>
          )}
          {/* Export to Excel Button */}
          <button 
            className="btn-export-excel"
            onClick={handleExportToExcel}
            disabled={reports.length === 0}
            title="Export to Excel"
          >
            <FaFileExcel /> Export to Excel
          </button>
        </div>
      </div>

      {/* Search and Filter Row */}
      <div className="search-filter-row">
        {/* Search Bar */}
        <div className="search-wrapper">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            className="search-input"
            placeholder="Search by customer name, PAN number, organization..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        {/* Filters Group */}
        <div className="filters-group">
          {/* Date Range Filter */}
          <div className="date-filter">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setCurrentPage(1);
              }}
              className="date-input"
              placeholder="From Date"
            />
            <span className="date-separator">to</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setCurrentPage(1);
              }}
              className="date-input"
              placeholder="To Date"
            />
          </div>
        </div>
      </div>

      <div className="table-container">
        {loading && (
          <div className="credit-reports-loading-message">Loading reports...</div>
        )}
        {!loading && filteredReports.length === 0 && (
          <div className="empty-state">
            <p>No reports found</p>
          </div>
        )}
        {!loading && filteredReports.length > 0 && (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Report ID</th>
                  <th>Customer Name</th>
                  <th>Organization Name</th>
                  <th>Orguser Name</th>
                  <th>Credit Score Type</th>
                  <th>Provider</th>
                  <th>Cost</th>
                  <th>Date &amp; Time</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredReports.map((report) => (
                  <tr key={report.id}>
                    <td>{report.reportId}</td>
                    <td>{report.customerName}</td>
                    <td>{report.organizationName}</td>
                    <td>{report.performedByName}</td>
                    <td>
                      <span className="plan-badge">{report.planType}</span>
                    </td>
                    <td>
                      <span className="provider-badge">
                        {getProviderLabel(report)}
                      </span>
                    </td>
                    <td>{report.creditCost}</td>
                    <td>
                      {report.createdAt
                        ? new Date(report.createdAt).toLocaleString('en-IN', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          })
                        : "-"}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon-view"
                          onClick={() => handleViewPdf(report)}
                          title="View PDF Report"
                        >
                          <FaEye />
                        </button>
                        <button
                          className="btn-icon-download"
                          onClick={() => handleDownload(report)}
                          title="Download PDF"
                        >
                          <FaDownload />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination-container">
                <div className="pagination-info">
                  Showing {(currentPage - 1) * reportsPerPage + 1} to{" "}
                  {Math.min(currentPage * reportsPerPage, totalRecords)} of {totalRecords}{" "}
                  records
                </div>
                <div className="pagination-buttons">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                    className="pagination-btn"
                  >
                    Previous
                  </button>
                  {Array.from({ length: totalPages }).map((_, i) => {
                    const pageNum = i + 1;
                    // Show first, last, current, and adjacent pages
                    if (
                      pageNum === 1 ||
                      pageNum === totalPages ||
                      (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={pageNum}
                          className={`pagination-btn ${currentPage === pageNum ? "active" : ""}`}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                      return <span key={pageNum}>...</span>;
                    }
                    return null;
                  })}
                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                    className="pagination-btn"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <PdfViewerModal
        isOpen={isPdfViewerOpen}
        toggle={() => setIsPdfViewerOpen(false)}
        fileUrl={pdfUrl}
        title="Credit Assessment Report"
      />
    </div>
  );
}

CreditReportManagement.propTypes = {
  role: PropTypes.oneOf(["superadmin", "admin", "orguser"]),
  authService: PropTypes.shape({
    getAuthHeader: PropTypes.func.isRequired,
  }).isRequired,
  apiBaseUrl: PropTypes.string,
};

CreditReportManagement.defaultProps = {
  role: "orguser",
  apiBaseUrl: process.env.REACT_APP_API_URL,
};

